"use client";

import cn from "@utils/cn";
import { getLink } from "@utils/linkUtils";
import { useLocale, useTranslations } from "next-intl";
import Image from "next/image";
import React, { useEffect, useRef, useState } from "react";

import useMediaQuery from "@/hooks/useMediaQuery";
import { MapTab } from "@/types";

import Button, { ButtonSize, ButtonTheme, ButtonVariant } from "../ui/Button/Button";
import MapComponent from "./Map";
import { MapComponentProps } from "./type";

const MapContainer: React.FC<MapComponentProps> = ({ entry, googleMapApiKey }) => {
  const customMapOptions: google.maps.MapOptions = {
    mapId: "fe01cd424133fe69",
    disableDoubleClickZoom: false,
    styles: null,
    scrollwheel: null,
    gestureHandling: "cooperative",
    minZoom: 2,
    restriction: {
      latLngBounds: {
        north: 85,
        south: -85,
        west: -180,
        east: 180,
      },
    },
  };
  const [map, setMap] = useState<google.maps.Map | undefined>();
  const [selectedTabIndex, setSelectedTabIndex] = useState<number>(0);
  const [isMapInteracted, setIsMapInteracted] = useState(false);
  const isMobile = useMediaQuery("(max-width: 1023px)");
  const locale = useLocale();
  const t = useTranslations();

  const onMapInit = (map: google.maps.Map) => {
    setMap(map);
    createCustomLabel(map);
  };
  const handleScroll = () => {
    if (isMapInteracted) {
      setIsMapInteracted(false);
    }
  };
  const handleMapInteraction = (event: any) => {
    setIsMapInteracted(true);
  };
  const handleTabClick = (tab: MapTab | null, index: number) => {
    setSelectedTabIndex(index);
    setIsMapInteracted(false);
  };
  const createCustomLabel = (map: google.maps.Map) => {
    const labelDiv = document.createElement("div");
    labelDiv.style.position = "absolute";
    labelDiv.style.color = "#858585";
    labelDiv.style.textAlign = "center";
    labelDiv.style.textShadow = `
    1px 1px 0 #fff,
    -1px -1px 0 #fff,
    1px -1px 0 #fff,
    -1px 1px 0 #fff`;

    const customOverlay = new google.maps.OverlayView();

    // UAE-specific label so that, as with Google, it is automatically translated based on where the user comes from
    const updateLabel = (placeId: string) => {
      const service = new google.maps.places.PlacesService(map);
      service?.getDetails({ placeId }, (place, status) => {
        if (status === google.maps.places.PlacesServiceStatus.OK && place) {
          labelDiv.innerText = place?.name || "";
        }
      });
    };

    const placeId = "ChIJvRKrsd9IXj4RpwoIwFYv0zM"; // UAE place id
    updateLabel(placeId);

    customOverlay.onAdd = () => {
      const overlayLayer = customOverlay?.getPanes()?.overlayLayer;
      if (overlayLayer) {
        overlayLayer?.appendChild(labelDiv);
      }
    };

    customOverlay.draw = () => {
      const projection = customOverlay.getProjection();
      const position = new google.maps.LatLng(25.074599677248674, 54.65758567917328);
      const point = projection.fromLatLngToDivPixel(position);

      if (point) {
        labelDiv.style.left = `${point.x}px`;
        labelDiv.style.top = `${point.y}px`;

        const currentZoom = map?.getZoom() || entry?.zoom;

        labelDiv.style.fontSize = currentZoom < 3 ? "10px" : "13px";
        labelDiv.style.fontWeight = currentZoom <= 2 ? "400" : currentZoom < 3 ? "500" : "600";
        labelDiv.style.width = currentZoom < 3 ? "55px" : "75px";
        labelDiv.style.transform = currentZoom < 3.5 ? "translate(-30%, -30%)" : "translate(-40%, -30%)";
        labelDiv.style.display = currentZoom >= 4 ? "none" : "inline-block";
      }
    };

    customOverlay.onRemove = () => {
      if (labelDiv.parentNode) {
        labelDiv?.parentNode?.removeChild(labelDiv);
      }
    };

    customOverlay.setMap(map);

    map.addListener("zoom_changed", () => {
      customOverlay?.draw();
    });
  };

  useEffect(() => {
    if (window) {
      window.addEventListener("scroll", handleScroll);

      return () => {
        window.removeEventListener("scroll", handleScroll);
      };
    }
  }, [isMapInteracted]);

  useEffect(() => {
    if (map && isMobile) {
      map.addListener("dragstart", handleMapInteraction);
      map.addListener("zoom_changed", handleMapInteraction);
    }
  }, [map, isMobile]);

  return (
    <div className="map-container relative w-full h-full">
      <div className="tabs flex sticky top-0 bg-white z-20 md:top-[var(--header-height)] overflow-x-scroll whitespace-nowrap custom-scrollbar">
        <div className="tab-list m-auto flex w-fit">
          {entry.tabs?.map((tab: any, index: any) => (
            <div key={index} className={cn(selectedTabIndex === index ? "border-b-[1px] border-b-black" : "")}>
              <button
                className={cn(selectedTabIndex === index ? "text-black" : "text-gray-30", "mx-s pt-m pb-s")}
                onClick={(e) => {
                  handleTabClick(tab, index);
                  e.currentTarget.scrollIntoView({ inline: "center", behavior: "smooth", block: "nearest" });
                }}
              >
                {tab.title}
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="map-wrapper h-full max-md:relative max-md:overflow-hidden">
        <MapComponent
          entry={entry}
          selectedTabIndex={selectedTabIndex}
          mapOptions={customMapOptions}
          googleMapApiKey={googleMapApiKey}
          onMapInit={onMapInit}
        />
        {entry.tabs[selectedTabIndex]?.modal && (
          <div
            className={`box-info rounded-2xl bg-gray-10 flex flex-col max-w-[420px] ${isMobile && isMapInteracted ? "move-down" : ""}`}
            onClick={() => isMobile && setIsMapInteracted(false)}
          >
            {entry.tabs[selectedTabIndex].modal.logo && (
              <Image
                src={entry.tabs[selectedTabIndex].modal.logo?.url || ""}
                width={entry.tabs[selectedTabIndex].modal.logo?.width || 100}
                height={entry.tabs[selectedTabIndex].modal.logo?.height || 100}
                alt={entry.tabs[selectedTabIndex].modal.logo?.title || ""}
                className="mx-auto mb-l"
              />
            )}
            <p className="text-m font-secondary-light leading-6">{entry.tabs[selectedTabIndex].modal.text}</p>
            {entry.tabs[selectedTabIndex].modal.cta && (
              <div className="mt-l flex justify-center">
                <Button
                  size={ButtonSize.MEDIUM}
                  theme={ButtonTheme.BLACK}
                  type="button"
                  variant={ButtonVariant.PRIMARY}
                  href={getLink(entry.tabs[selectedTabIndex].modal.cta || "", locale)}
                  target={entry.tabs[selectedTabIndex].modal.cta?.link.target}
                  customClass="w-full flex justify-center"
                >
                  {entry.tabs[selectedTabIndex].modal.cta?.label}
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MapContainer;
